import { useState, ChangeEvent, useEffect } from "react";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import OwButton from "@components/shared/OwButton/OwButton";
import { OwAccordion } from "@components/shared/OwAccordion/OwAccordion";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import { v4 as uuidv4 } from "uuid";
import { OwFnInput } from "@components/shared/OwFnInput/OwFnInput";
import OwSelectOption from "@components/shared/OwSelectOption/OwSelectOption";
import EndpointsEnum from "@models/EndpointsEnum";
import ICreateItemOptionDTO from "@models/DTO/ICreateItemOptionDTO";
import IUpdateItemOptionDTO from "@models/DTO/IUpdateItemOptionDTO";
import IOnCreateItemOptionDTO from "@models/DTO/IOnCreateItemOptionDTO";
import IOnUpdateItemOptionDTO from "@models/DTO/IOnUpdateItemOptionDTO";
import IDeleteItemOptionDTO from "@models/DTO/IDeleteItemOptionDTO";
import IOnDeleteItemOptionDTO from "@models/DTO/IOnDeleteItemOptionDTO";
import IUiSocketSetup from "@models/IUiSocketSetup";
import IOnGetCatalogItemOptionsDTO from "@models/DTO/IOnGetCatalogItemOptionsDTO";
import IGetCatalogItemOptionsDTO from "@models/DTO/IGetCatalogItemOptionsDTO";
import TitleTagEnum from "@models/TitleTagEnum";
import { useIntl } from "react-intl";
import { TypeaheadFn } from "@components/shared/TypeaheadFn/TypeaheadFn";
import IQBDDataExtDef from "@models/QuickBooks Desktop/IQBDDataExtDef";
import RestEndpointsEnum from "@models/RestEndpointsEnum";
import { buildRestUrlWithQuery, buildAuthHeadersForFetch } from "@services/RestService";
import OwToggle from "@components/shared/OwToggle/OwToggle";
import { useAppContextNonNull } from "@contexts/AppContext";
import INotification from "@models/INotification";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import NotificationPositionEnum from "@models/NotificationPositionEnum";
import SimpleConfirmModal from "@components/shared/SimpleConfirmModal/SimpleConfirmModal";
type Props = {
    catalogId: number;
    catalogGuid: string;
};

// Define our own interface that doesn't extend IItem_Option to avoid type issues
interface IItemOptionWithUuid {
    uuid: string;
    isNew?: boolean;
    catalog_id: number;
    name: string;
    description?: string;
    type: "dropdown" | "radio" | "checkbox" | "text" | "number";
    options?: { value: string; title: string }[];
    required: boolean;
    display_order?: number;
    qbd_custom_field?: IQBDDataExtDef;
    qbd_use_other_one?: boolean;
    qbd_use_other_two?: boolean;
    id: number;
    guid: string;
    created_at: string | Date;
    updated_at: string | Date;
}

const OPTION_TYPES = [
    { id: "dropdown", name: "Dropdown" },
    { id: "radio", name: "Radio" },
    { id: "checkbox", name: "Checkbox" },
    { id: "text", name: "Text" },
    { id: "number", name: "Number" },
];

export const ItemOptionsSidebar = (props: Props) => {
    const { uiContext, socket, userModel, supplierModel } = useAppContextNonNull();

    const intl = useIntl();

    const [itemOptions, setItemOptions] = useState<IItemOptionWithUuid[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [customFieldDefinitions, setCustomFieldDefinitions] = useState<IQBDDataExtDef[]>([]);

    const onGetCatalogItemOptions = (data: IOnGetCatalogItemOptionsDTO) => {
        const itemOptionsWithUuid = data.itemOptions.map((item) => ({
            ...item,
            uuid: uuidv4(),
            isNew: false,
            catalog_id: props.catalogId,
            id: item.id,
            guid: item.guid,
            created_at: item.created_at,
            updated_at: item.updated_at,
        }));

        setItemOptions(itemOptionsWithUuid);
    };

    const getCatalogItemOptions = () => {
        const data: IGetCatalogItemOptionsDTO = {
            catalogGuid: props.catalogGuid,
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        socket.emit(EndpointsEnum.CatalogItemOptionGetCatalogItemOptions, data);
    };

    // Lookup function for QuickBooks Desktop custom field definitions
    const lookupCustomFields = async (searchText: string) => {
        if (!supplierModel.Supplier_Integration?.has_qbd) return;

        try {
            const url = buildRestUrlWithQuery({
                endpoint: RestEndpointsEnum.QBDCustomFieldDefinitions,
                query: {
                    searchText,
                    assignToObject: "Item",
                },
            });

            const headers = buildAuthHeadersForFetch({
                userModel,
                supplierModel,
            });

            const response = await fetch(url, {
                method: "GET",
                headers,
            });

            const data = await response.json();
            setCustomFieldDefinitions(data.customFieldDefinitions || []);
        } catch (error) {
            console.error("Error fetching custom field definitions:", error);
            setCustomFieldDefinitions([]);
        }
    };

    const sockets: IUiSocketSetup[] = [{ endpoint: EndpointsEnum.CatalogItemOptionOnGetCatalogItemOptions, fn: onGetCatalogItemOptions }];

    useEffect(() => {
        sockets.forEach((item) => {
            socket.on(item.endpoint, item.fn);
        });

        getCatalogItemOptions();

        return () => {
            sockets.forEach((item) => {
                socket.off(item.endpoint, item.fn);
            });
        };
    }, []);

    // Add a new empty item option
    const addNewItemOption = () => {
        const newItemOption: IItemOptionWithUuid = {
            uuid: uuidv4(),
            catalog_id: props.catalogId,
            name: "",
            description: "",
            type: "dropdown",
            options: [],
            required: false,
            display_order: itemOptions.length,
            isNew: true,
            id: 0,
            guid: "",
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
        };

        setItemOptions([...itemOptions, newItemOption]);
    };

    // Remove an unsaved item option from local state
    const removeItemOption = (uuid: string) => {
        const updatedOptions = itemOptions.filter((option) => option.uuid !== uuid);
        setItemOptions(updatedOptions);
    };

    const closeDeleteModal = () => {
        uiContext.setModalComponent(null);
        uiContext.showModal(false);
    };

    const commitDeleteItemOption = (itemOption: IItemOptionWithUuid) => {
        closeDeleteModal();

        const deleteData: IDeleteItemOptionDTO = {
            itemOptionId: itemOption.id,
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        socket.emit(EndpointsEnum.CatalogItemOptionDeleteItemOption, deleteData);
        socket.once(EndpointsEnum.CatalogItemOptionOnDeleteItemOption, (data: IOnDeleteItemOptionDTO) => {
            setItemOptions((prev) => prev.filter((opt) => opt.id !== data.itemOptionId));
            const notification: INotification = {
                title: "item_option_deleted_successfully",
                level: NotificationLevelEnum.success,
                position: NotificationPositionEnum.bc,
            };
            uiContext.notifyFn(null, notification);
        });
    };

    // Delete a persisted item option via socket
    const deleteItemOption = (itemOption: IItemOptionWithUuid) => {
        if (itemOption.isNew) {
            removeItemOption(itemOption.uuid);
            return;
        }

        const modalComponent = (
            <SimpleConfirmModal
                message="confirm_delete_item_option"
                cancelFn={closeDeleteModal}
                confirmFn={() => commitDeleteItemOption(itemOption)}
            />
        );

        uiContext.setModalComponent(modalComponent);
        uiContext.showModal(true);
    };

    // Update an item option
    const updateItemOption = (uuid: string, field: string, value: any) => {
        const updatedOptions = itemOptions.map((option) => {
            if (option.uuid === uuid) {
                return {
                    ...option,
                    [field]: value,
                };
            }
            return option;
        });
        setItemOptions(updatedOptions);
    };

    // Add a new option value to an item option
    const addOptionValue = (uuid: string) => {
        const updatedOptions = itemOptions.map((option) => {
            if (option.uuid === uuid) {
                const currentOptions = option.options || [];
                return {
                    ...option,
                    options: [...currentOptions, { value: "", title: "" }],
                };
            }
            return option;
        });
        setItemOptions(updatedOptions);
    };

    // Remove an option value from an item option
    const removeOptionValue = (uuid: string, index: number) => {
        const updatedOptions = itemOptions.map((option) => {
            if (option.uuid === uuid && option.options) {
                const newOptions = [...option.options];
                newOptions.splice(index, 1);
                return {
                    ...option,
                    options: newOptions,
                };
            }
            return option;
        });
        setItemOptions(updatedOptions);
    };

    // Update an option value
    const updateOptionValue = (uuid: string, index: number, field: string, value: string) => {
        const updatedOptions = itemOptions.map((option) => {
            if (option.uuid === uuid && option.options) {
                const newOptions = [...option.options];
                newOptions[index] = {
                    ...newOptions[index],
                    [field]: value,
                };
                return {
                    ...option,
                    options: newOptions,
                };
            }
            return option;
        });
        setItemOptions(updatedOptions);
    };

    // Save all item options
    const saveItemOptions = () => {
        if (!socket) return;

        setIsLoading(true);

        const createPromises = itemOptions
            .filter((option) => option.isNew)
            .map((option) => {
                const { isNew, uuid, ...itemOption } = option;
                const createData: ICreateItemOptionDTO = {
                    catalogGuid: props.catalogGuid,
                    itemOption: {
                        name: itemOption.name,
                        description: itemOption.description,
                        type: itemOption.type as "dropdown" | "radio" | "checkbox" | "text" | "number",
                        options: itemOption.options,
                        required: itemOption.required,
                        display_order: itemOption.display_order,
                        qbd_custom_field: itemOption.qbd_custom_field,
                    },
                    userGuid: userModel.guid,
                    userToken: userModel.token,
                    supplierGuid: supplierModel.guid,
                };

                return new Promise((resolve) => {
                    socket.emit(EndpointsEnum.CatalogItemOptionCreateItemOption, createData);
                    socket.once(EndpointsEnum.CatalogItemOptionOnCreateItemOption, (data: IOnCreateItemOptionDTO) => {
                        resolve(data);
                    });
                });
            });

        const updatePromises = itemOptions
            .filter((option) => !option.isNew && option.id !== 0)
            .map((option) => {
                const { uuid, ...itemOption } = option;
                const updateData: IUpdateItemOptionDTO = {
                    itemOption: itemOption as any, // Cast to any to avoid type errors
                    userGuid: userModel.guid,
                    userToken: userModel.token,
                    supplierGuid: supplierModel.guid,
                };

                return new Promise((resolve) => {
                    socket.emit(EndpointsEnum.CatalogItemOptionUpdateItemOption, updateData);
                    socket.once(EndpointsEnum.CatalogItemOptionOnUpdateItemOption, (data: IOnUpdateItemOptionDTO) => {
                        resolve(data);
                    });
                });
            });

        // console.log("Promises", createPromises, updatePromises);

        Promise.all([...createPromises, ...updatePromises])
            .then(() => {
                setIsLoading(false);
                const notification: INotification = {
                    title: "item_options_saved_successfully",
                    level: NotificationLevelEnum.success,
                    position: NotificationPositionEnum.bc,
                };
                uiContext.notifyFn(null, notification);
                uiContext.toggleSidebar();
            })
            .catch((error) => {
                console.error("Error saving item options:", error);
                setIsLoading(false);
                const notification: INotification = {
                    title: "error_saving_item_options",
                    level: NotificationLevelEnum.error,
                    position: NotificationPositionEnum.bc,
                };
                uiContext.notifyFn(null, notification);
            });
    };

    // Handle input change events
    const handleInputChange = (e: ChangeEvent<HTMLInputElement>, uuid: string, field: string) => {
        updateItemOption(uuid, field, e.target.value);
    };

    // Handle select change events
    const handleSelectChange = (e: ChangeEvent<HTMLSelectElement>, uuid: string) => {
        updateItemOption(uuid, "type", e.target.value);
    };

    // Handle checkbox change events
    const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>, uuid: string) => {
        updateItemOption(uuid, "required", e.target.value);
    };

    return (
        <div className={"ItemOptionsSidebar p-2"}>
            <div className="d-flex align-items-center justify-content-between">
                <OwTitle leftIconName={"fad fa-boxes"} title={"item_options"} tag={"h3"} />
                <OwButton centerIconClass={"fas fa-times"} fn={() => uiContext.toggleSidebar()} className="btn-danger" />
            </div>

            <div className="my-3">
                <OwButton leftIconClass="fas fa-plus" title="add_item_option" fn={addNewItemOption} className="btn-primary btn-sm" />
            </div>

            <div className="item-options-container">
                {itemOptions.map((itemOption) => (
                    <OwAccordion
                        key={itemOption.uuid}
                        title={itemOption.name || intl.formatMessage({ id: "new_item_option" })}
                        className="mb-3"
                        fullWidthClick={true}
                        initOpen={itemOption.isNew}
                        disableTranslation={true}
                        rightElem={
                            <div className="ms-auto" onClick={(e) => e.stopPropagation()}>
                                <OwButton
                                    centerIconClass="fas fa-trash-alt"
                                    fn={() => deleteItemOption(itemOption)}
                                    className="btn-danger btn-sm me-2"
                                />
                            </div>
                        }
                        titleTag={TitleTagEnum.H5}>
                        <div className="p-2">
                            {/* <div className="d-flex justify-content-end mt-3">
                                <OwButton
                                    centerIconClass="fas fa-trash"
                                    fn={() => removeItemOption(itemOption.uuid)}
                                    className="btn-danger btn-sm me-2"
                                />
                            </div> */}
                            <div className="mb-3">
                                <OwLabel title="name" showIsReqAsterik={true} />
                                <OwFnInput
                                    name="name"
                                    value={itemOption.name}
                                    fn={(e: ChangeEvent<HTMLInputElement>) => handleInputChange(e, itemOption.uuid, "name")}
                                />
                            </div>

                            <div className="mb-3">
                                <OwLabel title="description" />
                                <OwFnInput
                                    name="description"
                                    value={itemOption.description || ""}
                                    fn={(e: ChangeEvent<HTMLInputElement>) => handleInputChange(e, itemOption.uuid, "description")}
                                />
                            </div>

                            <div className="mb-3">
                                <OwLabel title="type" showIsReqAsterik={true} />
                                <OwSelectOption
                                    name="type"
                                    valueProp="id"
                                    titleProp="name"
                                    options={OPTION_TYPES}
                                    fn={(e: ChangeEvent<HTMLSelectElement>) => handleSelectChange(e, itemOption.uuid)}
                                    selectedOption={itemOption}
                                    selectedOptionProp="type"
                                />
                            </div>

                            <div className="mb-3">
                                <OwToggle
                                    name="required"
                                    label="required"
                                    labelOnRight={true}
                                    active={itemOption.required}
                                    fn={(e: any) => handleCheckboxChange(e, itemOption.uuid)}
                                />
                            </div>

                            {supplierModel.Supplier_Integration?.has_qbd && (
                                <div className="mb-3">
                                    <div>
                                        <OwLabel title="qbd_custom_field" />
                                        <TypeaheadFn
                                            lookupFn={lookupCustomFields}
                                            availableOptions={customFieldDefinitions}
                                            selectedOption={itemOption.qbd_custom_field}
                                            selectedOptionFn={(field: IQBDDataExtDef) =>
                                                updateItemOption(itemOption.uuid, "qbd_custom_field", field)
                                            }
                                            titleProp="DataExtName"
                                            emitSelectionSetup={{ emitObject: true }}
                                            placeholder="Search QuickBooks custom fields..."
                                            clearSelectionFn={() => updateItemOption(itemOption.uuid, "qbd_custom_field", undefined)}
                                        />
                                        {itemOption.qbd_custom_field && (
                                            <div className="text-muted small mt-1">Selected: {itemOption.qbd_custom_field.DataExtName}</div>
                                        )}
                                    </div>
                                    <div>
                                        <OwToggle
                                            name="qbd_use_other_one"
                                            label="qbd_use_other_one"
                                            labelOnRight={true}
                                            active={itemOption.qbd_use_other_one || false}
                                            fn={(e: any) => updateItemOption(itemOption.uuid, "qbd_use_other_one", e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <OwToggle
                                            name="qbd_use_other_two"
                                            label="qbd_use_other_two"
                                            labelOnRight={true}
                                            active={itemOption.qbd_use_other_two || false}
                                            fn={(e: any) => updateItemOption(itemOption.uuid, "qbd_use_other_two", e.target.value)}
                                        />
                                    </div>
                                </div>
                            )}

                            {(itemOption.type === "dropdown" || itemOption.type === "radio") && (
                                <div className="mb-3">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <OwLabel title="options" />
                                        <OwButton
                                            centerIconClass="fas fa-plus"
                                            fn={() => addOptionValue(itemOption.uuid)}
                                            className="btn-secondary btn-sm"
                                        />
                                    </div>

                                    {(itemOption.options || []).map((option, index) => (
                                        <div key={index} className="d-flex mb-2 align-items-center">
                                            <div className="flex-grow-1 me-2">
                                                <OwFnInput
                                                    name={`value-${index}`}
                                                    placeholder="value"
                                                    value={option.value}
                                                    fn={(e: ChangeEvent<HTMLInputElement>) =>
                                                        updateOptionValue(itemOption.uuid, index, "value", e.target.value)
                                                    }
                                                />
                                            </div>
                                            <div className="flex-grow-1 me-2">
                                                <OwFnInput
                                                    name={`title-${index}`}
                                                    placeholder="title"
                                                    value={option.title}
                                                    fn={(e: ChangeEvent<HTMLInputElement>) =>
                                                        updateOptionValue(itemOption.uuid, index, "title", e.target.value)
                                                    }
                                                />
                                            </div>
                                            <OwButton
                                                centerIconClass="fas fa-times"
                                                fn={() => removeOptionValue(itemOption.uuid, index)}
                                                className="btn-danger btn-sm"
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </OwAccordion>
                ))}
            </div>

            <div className="d-flex justify-content-end mt-4">
                <OwButton title="cancel" className="btn-secondary me-2" fn={() => uiContext.toggleSidebar()} />
                <OwButton title="save" className="btn-primary" fn={saveItemOptions} disabled={isLoading} active={isLoading} />
            </div>
        </div>
    );
};
